import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Typography } from '@glplus/glpweb-dsl';
import { CATEGORIES } from '../kit/content';
import { IRS_LINKS } from '../kit/irsLinks';
import { KitDisclaimer } from '../kit/Disclaimer';
import { KitIcon } from '../kit/KitIcon';
import { downloadCsv, MILEAGE_LOG, EXPENSE_TRACKER } from '../kit/tools/csv';
import type { Category, Lang } from '../kit/types';
import { ORANGE_LINK_STYLE } from '../styles/colors';

/**
 * Runner's Kit hub — deck §10 (`135:4`): business half, "On the job" half, the
 * En Español card, Tools & templates, official IRS forms. Static by design
 * (spec §16): no backend, no shared repo.
 */
export default function Kit() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language.startsWith('es') ? 'es' : 'en') as Lang;
  const business = CATEGORIES.filter((c) => c.half === 'business');
  const work = CATEGORIES.filter((c) => c.half === 'work');

  return (
    <Box data-testid="page-kit">
      <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: '#1f2937', fontSize: { xs: '24px', md: '30px' } }}>{t('kit.title')}</Typography>
      <Typography sx={{ color: '#4b5563', mb: 2.5 }}>{t('kit.subtitle')}</Typography>
      {/* 🔒 Category colours are semantic (Rod 2026-09-10): red / amber / green / navy — the same four the app uses. */}
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 2.5 }}>{t('kit.legend')}</Typography>
      <KitDisclaimer />
      <Typography component={Link} to="/handbook" data-testid="kit-handbook-link" sx={{ display: 'block', mb: 2.5, p: 2, borderRadius: 2.5, backgroundColor: '#fff', borderLeft: '4px solid #343F5B', color: '#343F5B', fontWeight: 700, fontSize: '14px', textDecoration: 'none', boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>{t('handbook.linkFromKit')} →</Typography>

      <Grid>{business.map((c) => <CategoryCard key={c.slug} c={c} lang={lang} />)}
        <IrsCard />
      </Grid>

      <Typography sx={{ fontWeight: 800, color: '#1f2937', mt: 4, mb: 2, fontSize: { xs: '22px', md: '26px' } }}>{t('kit.work')}</Typography>
      <Grid>{work.map((c) => <CategoryCard key={c.slug} c={c} lang={lang} />)}
        <Card accent="#F67A1A">
          <KitIcon category={{ ...work[0], accent: '#F67A1A', icon: 'dollar' }} />
          <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937', mt: 1.5 }}>{t('kit.espanol.title')}</Typography>
          <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 0.5, flex: 1 }}>{t('kit.espanol.body')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
            <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{t('kit.espanol.count')}</Typography>
            <Typography component="button" onClick={() => void i18n.changeLanguage(lang === 'es' ? 'en' : 'es')} sx={{ ...ORANGE_LINK_STYLE, background: 'none', border: 0, cursor: 'pointer', p: 0, fontFamily: 'inherit' }}>{t('kit.espanol.cta')}</Typography>
          </Box>
        </Card>
      </Grid>

      {/* Tools & templates */}
      <Box sx={{ mt: 4, backgroundColor: '#fff', borderRadius: 2.5, borderTop: '3px solid #2E9E3A', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1f2937' }}>{t('kit.tools.title')}</Typography>
          <Chip label={t('kit.tools.chip')} size="small" backgroundColor="#dcfce7" textColor="#15803d" fontWeight={700} />
        </Box>
        <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 0.5, mb: 2 }}>{t('kit.tools.subtitle')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          <ToolRow glyph="⟐" title={t('kit.tools.mileage.title')} sub={t('kit.tools.mileage.sub')} cta={t('kit.tools.download')} onClick={() => downloadCsv(lang === 'es' ? 'registro-de-millas.csv' : 'mileage-log.csv', MILEAGE_LOG[lang])} />
          <ToolRow glyph="▤" title={t('kit.tools.expense.title')} sub={t('kit.tools.expense.sub')} cta={t('kit.tools.download')} onClick={() => downloadCsv(lang === 'es' ? 'registro-de-gastos.csv' : 'expense-tracker.csv', EXPENSE_TRACKER[lang])} />
          <ToolRow glyph="%" title={t('kit.tools.estimator.title')} sub={t('kit.tools.estimator.sub')} cta={t('kit.tools.open')} to="/kit/tools#estimator" />
          <ToolRow glyph="✓" title={t('kit.tools.checklist.title')} sub={t('kit.tools.checklist.sub')} cta={t('kit.tools.open')} to="/kit/tools#checklist" />
        </Box>
      </Box>

      {/* IRS forms */}
      <Box id="irs" sx={{ mt: 3, backgroundColor: '#fff', borderRadius: 2.5, borderTop: '3px solid #343F5B', p: { xs: 2, md: 3 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#1f2937', mb: 1.5 }}>{t('kit.irs.title')}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
          {IRS_LINKS.map((l) => (
            <Box key={l.key} component="a" href={l.url} target="_blank" rel="noopener noreferrer" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, backgroundColor: '#f8fafc', textDecoration: 'none' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '14px' }}>{l.label[lang]}</Typography>
                <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{l.form}</Typography>
              </Box>
              <Typography sx={{ color: '#343F5B', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>{t('kit.irs.cta')}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' }, gap: 2 }}>{children}</Box>;
}
function Card({ accent, children }: { accent: string; children: React.ReactNode }) {
  return <Box sx={{ backgroundColor: '#fff', borderRadius: 2.5, borderTop: `3px solid ${accent}`, p: 2.5, display: 'flex', flexDirection: 'column', boxShadow: '0 1px 2px rgba(16,24,40,0.06)' }}>{children}</Box>;
}
function CategoryCard({ c, lang }: { c: Category; lang: Lang }) {
  const { t } = useTranslation();
  const n = c.guides.length;
  return (
    <Card accent={c.accent}>
      <KitIcon category={c} />
      <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937', mt: 1.5 }}>{c.label[lang]}</Typography>
      <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 0.5, flex: 1 }}>{c.blurb[lang]}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{n === 1 ? t('kit.guide') : t('kit.guides', { count: n })}</Typography>
        <Typography component={Link} to={`/kit/${c.slug}`} sx={{ color: '#343F5B', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}>{t('kit.read')}</Typography>
      </Box>
    </Card>
  );
}
function IrsCard() {
  const { t, i18n } = useTranslation();
  const es = i18n.language.startsWith('es');
  return (
    <Card accent="#5f6b7a">
      <Box sx={{ width: 40, height: 40, borderRadius: 2, backgroundColor: '#5f6b7a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>↗</Box>
      <Typography sx={{ fontWeight: 800, fontSize: '17px', color: '#1f2937', mt: 1.5 }}>{es ? 'Formularios del IRS y enlaces oficiales' : 'IRS forms & official links'}</Typography>
      <Typography sx={{ color: '#4b5563', fontSize: '14px', mt: 0.5, flex: 1 }}>{es ? 'Directo a la fuente — los formularios federales exactos que necesitarás, en IRS.gov.' : "Straight to the source — the exact federal forms you'll need, on IRS.gov."}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5 }}>
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{t('kit.links')}</Typography>
        <Typography component="a" href="#irs" sx={{ color: '#343F5B', fontWeight: 700, fontSize: '13px', textDecoration: 'none' }}>{t('kit.read')}</Typography>
      </Box>
    </Card>
  );
}
function ToolRow({ glyph, title, sub, cta, onClick, to }: { glyph: string; title: string; sub: string; cta: string; onClick?: () => void; to?: string }) {
  const btn = <Button ariaLabel={`${cta} — ${title}`} buttonText={cta} variant="outlined" size="small" onClick={onClick} sx={{ backgroundColor: '#fff', borderColor: '#d0d5dd', color: '#1f2937', fontWeight: 700 }} />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, backgroundColor: '#f8fafc' }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 2, backgroundColor: '#2E9E3A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, flexShrink: 0 }} aria-hidden="true">{glyph}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: '#1f2937', fontSize: '14px' }}>{title}</Typography>
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{sub}</Typography>
      </Box>
      {to ? <Link to={to} style={{ textDecoration: 'none' }}>{btn}</Link> : btn}
    </Box>
  );
}
